// import { firestore, TRANSACTIONS_COLL } from "_firebase";

import {
    getTransactions,
    addTransactionToHistory as addTransactionToHistoryAPI,
    transferLegacyCredits as transferLegacyCreditsAPI,
} from "utils/payment/api";

import {
    fetchingTransactionsStart,
    fetchingTransactionsSuccess,
    fetchingTransactionsError,
    addingNewTransactionError,
    addingNewTransactionStart,
    addingNewTransactionSuccess,
} from "./actions";
import { change } from "store/root/actions";

export const fetchTransactions = (uid, filters, lastId, isLoadingMore) => {
    return async (dispatch, getState) => {
        dispatch(fetchingTransactionsStart());
        try {
            if (!uid) {
                throw new Error("User ID is required to fetch transactions.");
            }

            const response = await getTransactions(uid, filters, lastId);

            // DashboardAPI returns the axios error object instead of throwing
            if (!response?.data || response?.isAxiosError) {
                const message =
                    response?.response?.data?.message || response?.message || "Failed to fetch transactions";
                throw Object.assign(new Error(message), { response: response?.response });
            }

            const { transactions = [], hasMore = false, lastId: newLastId = null } = response.data;

            if (isLoadingMore) {
                const {
                    payments: { transactions: existingTransactions = [] },
                } = getState();
                const updatedTransactions = [...existingTransactions, ...transactions];

                dispatch(fetchingTransactionsSuccess(updatedTransactions));
            } else {
                dispatch(fetchingTransactionsSuccess(transactions));
            }

            return { hasMore, lastId: newLastId };
        } catch (error) {
            console.error("Error fetching transactions:", error);
            dispatch(fetchingTransactionsError(error));
        }
    };
};

/*
[transactionId]: {
        user: {
            userId: abc123,
            username: Joe Doe
        }
        transactionType: "SPENT.PARAMETRIC" // PURCHASED, TRANSFER_IN, TRANSFER_OUT, SPENT.INSIGHTS
        /// If it was a transfer
        transfer: {
            feature: "LEGACY_TRANSFER" // USER_TRANSFER
            user: {
                userId: abc123,
                username: Joe Doe
            }
        }
        ///If it was spent in a paid feature
        spent: {
            feature: "INSIGHTS" // PARAMETRIC or any other paid feature
            url: "/parametric" // /model 
            id: abc123 //
        }
        amountInCents: 123,
        creditAmount: 123,
        data: 123,
        receiptUrl: jfkds.com
        transactionId: 123
    }
*/

export const addTransactionToHistory = ({ type, credits, spentDetails = {}, transferDetails = {} }) => {
    return async (dispatch, getState) => {
        try {
            dispatch(addingNewTransactionStart());

            const { user: { uid } = {} } = getState() || {};

            if (!uid) {
                const error = new Error("No user");
                dispatch(addingNewTransactionError(error));
                throw error;
            }

            const response = await addTransactionToHistoryAPI({
                userUid: uid,
                type,
                credits,
                spentDetails,
                transferDetails,
            });

            // DashboardAPI returns the axios error object instead of throwing
            if (!response?.data || response?.isAxiosError) {
                const payload = response?.response?.data || {};
                const status = response?.response?.status;
                const serverCredits = payload?.creditSummary?.credits;

                if (serverCredits != null) {
                    dispatch(change("user.creditSummary.credits", serverCredits));
                }

                const message =
                    payload?.message ||
                    (status === 403 ? "Insufficient credits" : null) ||
                    response?.message ||
                    "Transaction failed";

                throw Object.assign(new Error(message), { status, payload });
            }

            const { creditSummary: { total: newLegacyCredits = 0, credits: newUserCredits = 0 } = {}, availableRuns } =
                response.data;

            dispatch(change("user.creditSummary.total", newLegacyCredits));
            dispatch(change("user.creditSummary.credits", newUserCredits));

            if (availableRuns != null) {
                dispatch(
                    change("parametricAnalysis.parametricAnalysis.parametricDetails.availableRuns", availableRuns)
                );
                dispatch(
                    change(
                        "parametricAnalysis.parametricAnalysisToCompare.parametricDetails.availableRuns",
                        availableRuns
                    )
                );
            }

            dispatch(addingNewTransactionSuccess());
            return response.data;
        } catch (error) {
            dispatch(addingNewTransactionError(error));
            throw error;
        }
    };
};

export const transferLegacyCredits = () => {
    return async (dispatch) => {
        try {
            dispatch(addingNewTransactionStart());

            const response = await transferLegacyCreditsAPI();

            // DashboardAPI returns the axios error object instead of throwing
            if (!response?.data || response?.isAxiosError) {
                const message =
                    response?.response?.data?.message || response?.message || "Legacy credit transfer failed";
                throw Object.assign(new Error(message), { response: response?.response });
            }

            const { creditSummary: { total, credits: newCredits } = {} } = response.data;

            if (total != null) dispatch(change("user.creditSummary.total", total));
            if (newCredits != null) dispatch(change("user.creditSummary.credits", newCredits));

            dispatch(addingNewTransactionSuccess());
            return response.data;
        } catch (error) {
            dispatch(addingNewTransactionError(error));
            throw error;
        }
    };
};
